/* global axios */
import ApiClient from './ApiClient';
import { requestConfig, resourcePath } from './lais/httpHelpers';
import { redactLaisPayload } from './lais/redaction';

const isObject = value =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const redactText = value =>
  typeof value === 'string' ? redactLaisPayload(value) : value;

const normalizeAxis = axis => {
  if (!isObject(axis)) return {};

  return {
    ...axis,
    left: redactText(axis.left),
    right: redactText(axis.right),
    note: redactText(axis.note),
  };
};

const normalizeTrait = trait => {
  if (!isObject(trait)) return {};

  return {
    ...trait,
    is: redactText(trait.is),
    is_not: redactText(trait.is_not),
  };
};

const normalizeSection = section => {
  if (!isObject(section)) return {};

  return {
    ...section,
    title: redactText(section.title),
    body_text: redactText(section.body_text),
    body: Array.isArray(section.body)
      ? section.body.map(block => redactLaisPayload(block))
      : section.body,
    metadata: redactLaisPayload(section.metadata || {}),
  };
};

const normalizeGuideVersion = version => {
  if (!isObject(version)) return {};

  return {
    ...version,
    title: redactText(version.title),
    summary: redactText(version.summary),
    sections: Array.isArray(version.sections)
      ? version.sections.map(normalizeSection)
      : [],
    metadata: redactLaisPayload(version.metadata || {}),
  };
};

export const normalizeBrandVoiceFields = data => {
  if (!isObject(data)) return {};

  return {
    ...data,
    persona: redactText(data.persona),
    tone: redactText(data.tone),
    examples: redactText(data.examples),
    avoid_words: redactText(data.avoid_words),
    greeting: redactText(data.greeting),
    signature: redactText(data.signature),
    default_personality_axes: Array.isArray(data.default_personality_axes)
      ? data.default_personality_axes.map(normalizeAxis)
      : [],
    personality_axes: Array.isArray(data.personality_axes)
      ? data.personality_axes.map(normalizeAxis)
      : [],
    default_voice_traits: Array.isArray(data.default_voice_traits)
      ? data.default_voice_traits.map(normalizeTrait)
      : [],
    voice_traits: Array.isArray(data.voice_traits)
      ? data.voice_traits.map(normalizeTrait)
      : [],
  };
};

export const normalizeBrandVoiceGuide = data => {
  if (!isObject(data)) return {};

  return {
    ...data,
    current_version: normalizeGuideVersion(data.current_version),
    versions: Array.isArray(data.versions)
      ? data.versions.map(normalizeGuideVersion)
      : [],
    default_sections: Array.isArray(data.default_sections)
      ? data.default_sections.map(normalizeSection)
      : [],
  };
};

const withNormalizedFields = request =>
  request.then(response => ({
    ...response,
    data: normalizeBrandVoiceFields(response.data),
  }));

const withNormalizedGuide = request =>
  request.then(response => ({
    ...response,
    data: normalizeBrandVoiceGuide(response.data),
  }));

class BrandVoiceAPI extends ApiClient {
  constructor() {
    super('brand_voice', { accountScoped: true });
  }

  get() {
    return withNormalizedFields(axios.get(this.url, requestConfig()));
  }

  update(payload) {
    return axios.patch(this.url, payload, requestConfig());
  }

  guide(params = {}) {
    return withNormalizedGuide(
      axios.get(resourcePath(this.url, 'guide'), requestConfig(params))
    );
  }

  updateGuide(guide) {
    return withNormalizedGuide(
      axios.patch(resourcePath(this.url, 'guide'), { guide }, requestConfig())
    );
  }

  publishGuide({ versionId }) {
    return withNormalizedGuide(
      axios.post(
        resourcePath(this.url, 'guide', 'publish'),
        { version_id: versionId },
        requestConfig()
      )
    );
  }
}

export default new BrandVoiceAPI();
